/**
 * Error-category vocabulary shared by backend classification
 * (backend/src/security/errorClassification.ts), the frontend's error handling
 * (apollo.ts keys recovery behavior off these codes), and tests. Single source
 * of truth — never string-match these values inline.
 */
export type ErrorCategory =
  | "UNAUTHENTICATED"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "RATE_LIMITED"
  | "UPSTREAM_UNAVAILABLE";

/** Stable, body-free messages — clients key off the code; this is the fallback text. */
export const CATEGORY_MESSAGES: Record<ErrorCategory, string> = {
  UNAUTHENTICATED: "Your provider session has expired. Reconnect to continue.",
  FORBIDDEN: "You don't have access to this resource on the connected provider.",
  NOT_FOUND: "The requested project, pipeline, or job was not found.",
  RATE_LIMITED: "The connected provider is rate limiting requests. Please retry shortly.",
  UPSTREAM_UNAVAILABLE: "The connected provider is temporarily unavailable. Please retry.",
};

export function isErrorCategory(value: unknown): value is ErrorCategory {
  return typeof value === "string" && value in CATEGORY_MESSAGES;
}

export type ProviderConnectionState = "connected" | "reconnect_required";
export type ProviderAccessState = "allowed" | "denied" | "unknown";
export type ProviderAvailability = "available" | "rate_limited" | "temporarily_unavailable";

export type ProviderState = {
  generation: string;
  connection: ProviderConnectionState;
  access: ProviderAccessState;
  availability: ProviderAvailability;
};

export type ProviderStateEvent =
  | { type: "switch"; generation: string }
  | { type: "provider_error"; generation: string; category: ErrorCategory }
  | { type: "refresh_failed"; generation: string }
  | { type: "refresh_succeeded"; generation: string }
  | { type: "access_allowed"; generation: string }
  | { type: "provider_recovered"; generation: string };

export type VisibleProviderStatus =
  | "reconnect_required"
  | "access_denied"
  | "rate_limited"
  | "temporarily_unavailable"
  | null;

export function initialProviderState(generation: string): ProviderState {
  return {
    generation,
    connection: "connected",
    access: "unknown",
    availability: "available",
  };
}

/**
 * Provider errors update only their owning axis. A raw 401 is a refresh trigger,
 * not reconnect authority; only refresh_failed may change connection state.
 */
export function advanceProviderState(
  state: ProviderState,
  event: ProviderStateEvent
): ProviderState {
  if (event.type === "switch") return initialProviderState(event.generation);
  if (event.generation !== state.generation) return state;

  switch (event.type) {
    case "provider_error":
      if (event.category === "FORBIDDEN") return { ...state, access: "denied" };
      if (event.category === "RATE_LIMITED") {
        return { ...state, availability: "rate_limited" };
      }
      if (event.category === "UPSTREAM_UNAVAILABLE" && state.availability !== "rate_limited") {
        return { ...state, availability: "temporarily_unavailable" };
      }
      return state;
    case "refresh_failed":
      return { ...state, connection: "reconnect_required" };
    case "refresh_succeeded":
      return { ...state, connection: "connected" };
    case "access_allowed":
      return { ...state, access: "allowed" };
    case "provider_recovered":
      return { ...state, availability: "available" };
  }
}

/** Exactly one user-facing operational status, with actionable axes first. */
export function visibleProviderStatus(state: ProviderState): VisibleProviderStatus {
  if (state.connection === "reconnect_required") return "reconnect_required";
  if (state.access === "denied") return "access_denied";
  if (state.availability === "rate_limited") return "rate_limited";
  if (state.availability === "temporarily_unavailable") {
    return "temporarily_unavailable";
  }
  return null;
}

export function providerImpactForErrorCategory(category: ErrorCategory): {
  access?: ProviderAccessState;
  availability?: ProviderAvailability;
} {
  if (category === "FORBIDDEN") return { access: "denied" };
  if (category === "RATE_LIMITED") return { availability: "rate_limited" };
  if (category === "UPSTREAM_UNAVAILABLE") {
    return { availability: "temporarily_unavailable" };
  }
  return {};
}
